import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookOpen, Upload, FileText, CheckCircle2, AlertCircle, Loader2, Lock } from "lucide-react";

import Modal from "../../../components/Modal";
import { describeApiError } from "../../../lib/apiError";
import LogbookTermsGate, { LogbookTermsModal } from "../components/LogbookTermsGate";
import LogbookChat from "../components/LogbookChat";
import WeekDropdown from "../components/WeekDropdown";
import WeekCalendar from "../components/WeekCalendar";
import StatusBadge from "../components/StatusBadge";
import {
  AssessmentPaymentClosedNotice,
  isAssessmentPaymentOpen,
} from "../components/PayAssessmentButton";

import {
  useGetMyLogbooksQuery,
  useGetLogbookWeekEntriesQuery,
  useSaveEntryMutation,
  useSearchSupervisorQuery,
  useRequestSupervisorMutation,
  useCancelSupervisorRequestMutation,
  useGetMySupervisorPendingStatusQuery,
  useGetTrainingCodesQuery,
  useUploadLogbookReportMutation,
  useInitiateAssessmentFeeMutation,
  useGetLogbookWeeksStatusQuery,
  useRespondToEntryCommentMutation,
} from "../api/logbookApi";

import {
  Day,
  DAYS,
  DAY_TO_NUM,
  NUM_TO_DAY,
  safeDate,
  fullNameFromSupervisor,
  computeStatus,
} from "../utils/logbook";

function safeMsg(x: any) {
  return String(
    x?.data?.message ||
      x?.message ||
      x?.error ||
      x?.data?.error ||
      "Something went wrong",
  );
}

// ✅ normalize supervisor search results to always be an array
function normalizeList(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

export default function LogbookDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const {
    data: logbooks = [],
    isLoading,
    error,
    refetch,
  } = useGetMyLogbooksQuery();

  const logbook = useMemo(
    () => logbooks.find((x: any) => String(x.id) === String(id)),
    [logbooks, id],
  );

  const [selectedWeek, setSelectedWeek] = useState(1);

  // The scheme's terms, reopenable for the whole life of the logbook — a
  // candidate in week thirty checking how often the book must be signed
  // (clause 4.14) should not have to go looking for a PDF.
  const [termsOpen, setTermsOpen] = useState(false);

  // ── Logbook report upload (PDF, max 5 pages) ──────────────────────────────
  const reportInputRef = useRef<HTMLInputElement | null>(null);
  const [reportErr, setReportErr] = useState<string | null>(null);
  const [reportMsg, setReportMsg] = useState<string | null>(null);
  const [uploadReport, { isLoading: uploadingReport }] =
    useUploadLogbookReportMutation();

  const onPickReport = async (file?: File | null) => {
    setReportErr(null);
    setReportMsg(null);
    if (!file || !id) return;

    if (file.type !== "application/pdf") {
      setReportErr("The report must be a PDF file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setReportErr("The report must be 10MB or smaller.");
      return;
    }

    try {
      const res: any = await uploadReport({ logbookId: id, file }).unwrap();
      setReportMsg(res?.message || "Report uploaded successfully.");
      refetch();
    } catch (e: any) {
      setReportErr(safeMsg(e));
    } finally {
      if (reportInputRef.current) reportInputRef.current.value = "";
    }
  };

  // ── Assessment fee (pay-for-assessment after submission) ──────────────────
  const [initiateAssessmentFee, { isLoading: payingAssessment }] =
    useInitiateAssessmentFeeMutation();
  const [assessmentPayError, setAssessmentPayError] = useState<string | null>(
    null,
  );

  const handlePayAssessment = async () => {
    if (!id) return;
    setAssessmentPayError(null);
    try {
      const res: any = await initiateAssessmentFee({ logbookId: id }).unwrap();

      if (!res?.merchant_reg_id || !res?.merch_trans_id) {
        setAssessmentPayError("Invalid payment response. Please try again.");
        return;
      }

      // Build and submit the OnePay POST form (same flow as other NIQS fees).
      const form = document.createElement("form");
      form.method = "POST";
      form.action = res.payment_url || "https://vuvaa.com/ONEPAY/mod/live/main";

      const fields: Record<string, any> = {
        merchant_reg_id: res.merchant_reg_id,
        amt_paid: res.amt_paid,
        merch_trans_id: res.merch_trans_id,
        client_phone: res.client_phone,
        product_desc: res.product_desc,
        client_email: res.client_email,
        client_name: res.client_name,
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = String(value ?? "");
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (e: any) {
      setAssessmentPayError(safeMsg(e));
    }
  };

  const [status, setStatus] = useState<
    "none" | "pending" | "approved" | "rejected"
  >("none");

  useEffect(() => {
    if (!logbook) return;
    setStatus(computeStatus(logbook));
  }, [logbook]);

  // `currentData`, NOT `data`. RTK Query's `data` is the last result this hook
  // fetched SUCCESSFULLY for ANY arg, so it outlives a change of week — and the
  // server refuses every week past `position + 1` with a 403 ("Week N is not
  // yet available"). Reading `data` meant the effect below refilled the form
  // from the last week that did load: on a logbook sitting at week 32, weeks
  // 34..52 every one of them showed week 32's five entries under their own
  // heading, and nothing changed as you clicked along the grid.
  // `currentData` is undefined until THIS week answers, so a refused or empty
  // week renders as a refused or empty week.
  const {
    currentData: weekData,
    isFetching: weekLoading,
    error: weekError,
    refetch: refetchWeek,
  } = useGetLogbookWeekEntriesQuery(
    { logbook_id: id || "", week: selectedWeek },
    {
      skip: !logbook || status !== "approved" || !id,
      refetchOnMountOrArgChange: true,
    },
  );

  // The server's own words for a week the candidate has not reached yet. The
  // grid offers all `size` weeks, but only up to `position + 1` can be opened,
  // and without this the locked ones look like an ordinary empty week.
  const weekBlocked = useMemo(() => {
    const err: any = weekError;
    if (!err) return null;
    if (err.status === 403) {
      return (
        err?.data?.message ||
        "This week is not open yet. Complete the weeks before it first."
      );
    }
    return "This week could not be loaded. Please try again.";
  }, [weekError]);

  const { data: trainingCodes = [] } = useGetTrainingCodesQuery();
  console.log(
    trainingCodes,
    "this is he forrr*************************************",
  );
  const [saveEntry, { isLoading: saving }] = useSaveEntryMutation();
  const [sendSupervisorRequest, { isLoading: requesting }] =
    useRequestSupervisorMutation();
  const [cancelSupervisorRequest, { isLoading: cancelling }] =
    useCancelSupervisorRequestMutation();

  /** ✅ Pending supervisor request check */
  const {
    data: pendingStatus,
    isFetching: pendingChecking,
    refetch: refetchPendingStatus,
  } = useGetMySupervisorPendingStatusQuery(
    { logbook_id: logbook?.id },
    { skip: !logbook?.id },
  );

  const hasPendingSupervisorRequest = Boolean(pendingStatus?.has_pending);
  const pendingRequest = pendingStatus?.pending_request || null;
  const pendingSupervisor = pendingRequest?.supervisor || null;

  /** Supervisor live search */
  const [supQuery, setSupQuery] = useState("");
  const [changingSupervisor, setChangingSupervisor] = useState(false);
  const shouldSearch = supQuery.trim().length >= 3;

  const { data: supRaw, isFetching: supLoading } = useSearchSupervisorQuery(
    supQuery,
    {
      skip:
        !shouldSearch ||
        (status !== "none" && !changingSupervisor) ||
        hasPendingSupervisorRequest ||
        pendingChecking,
    },
  );

  const supResults = useMemo(() => normalizeList(supRaw), [supRaw]);

  /** Daily fields */
  const [dailyText, setDailyText] = useState<Record<Day, string>>({
    Monday: "",
    Tuesday: "",
    Wednesday: "",
    Thursday: "",
    Friday: "",
  });

  const [dailyDate, setDailyDate] = useState<Record<Day, string>>({
    Monday: "",
    Tuesday: "",
    Wednesday: "",
    Thursday: "",
    Friday: "",
  });

  const [dailyHours, setDailyHours] = useState<Record<Day, string>>({
    Monday: "",
    Tuesday: "",
    Wednesday: "",
    Thursday: "",
    Friday: "",
  });

  const [dailySubject, setDailySubject] = useState<Record<Day, string>>({
    Monday: "",
    Tuesday: "",
    Wednesday: "",
    Thursday: "",
    Friday: "",
  });

  const [entryMeta, setEntryMeta] = useState<
    Record<
      Day,
      { id?: string; status?: string; updated_at?: string; entry_date?: string }
    >
  >({
    Monday: {},
    Tuesday: {},
    Wednesday: {},
    Thursday: {},
    Friday: {},
  });

  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  useEffect(() => {
    const txt: Record<Day, string> = {
      Monday: "",
      Tuesday: "",
      Wednesday: "",
      Thursday: "",
      Friday: "",
    };
    const dt: Record<Day, string> = {
      Monday: "",
      Tuesday: "",
      Wednesday: "",
      Thursday: "",
      Friday: "",
    };
    const hr: Record<Day, string> = {
      Monday: "",
      Tuesday: "",
      Wednesday: "",
      Thursday: "",
      Friday: "",
    };
    const subj: Record<Day, string> = {
      Monday: "",
      Tuesday: "",
      Wednesday: "",
      Thursday: "",
      Friday: "",
    };

    const meta: Record<
      Day,
      { id?: string; status?: string; updated_at?: string; entry_date?: string }
    > = {
      Monday: {},
      Tuesday: {},
      Wednesday: {},
      Thursday: {},
      Friday: {},
    };

    if (weekData?.entries?.length) {
      weekData.entries.forEach((e: any) => {
        const day = NUM_TO_DAY[e.day];
        if (!day) return;

        txt[day] = e.activity || "";
        dt[day] = (e.entry_date || "").slice(0, 10);
        hr[day] = String(e.hours ?? "");
        subj[day] = e.subject ? String(e.subject) : "";
        meta[day] = {
          id: e.id,
          status: e.status,
          updated_at: e.updated_at,
          entry_date: e.entry_date,
        };
      });
    }

    setDailyText(txt);
    setDailyDate(dt);
    setDailyHours(hr);
    setDailySubject(subj);
    setEntryMeta(meta);
  }, [weekData, selectedWeek, logbook?.id]);

  const handleSaveDay = async (day: Day) => {
    if (!logbook) return;

    const payload = {
      logbook_id: logbook.id,
      entry_date: dailyDate[day],
      activity: dailyText[day],
      hours: Number(dailyHours[day] || 0),
      week: selectedWeek,
      day: DAY_TO_NUM[day],
      subject: dailySubject[day] ? Number(dailySubject[day]) : null,
    };

    try {
      const res: any = await saveEntry(payload).unwrap();

      if (!res?.entry?.id) {
        setModal({
          open: true,
          title: "Not Saved",
          message: res?.message || "Save did not complete.",
        });
        return;
      }

      setModal({
        open: true,
        title: "Saved",
        message: `${day} entry saved successfully.`,
      });

      await refetchWeek();
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  const requestSupervisor = async (m: any) => {
    if (!logbook) return;

    if (hasPendingSupervisorRequest) {
      setModal({
        open: true,
        title: "Request Pending",
        message:
          "You already have a pending supervisor request. Please wait for it to be accepted or rejected before sending another request.",
      });
      return;
    }

    try {
      const res: any = await sendSupervisorRequest({
        logbook_id: logbook.id,
        supervisor_id: m.id,
        message: "Requesting supervision",
      }).unwrap();

      setModal({
        open: true,
        title: "Request Sent",
        message: res?.message || `Request sent to ${m.name || "supervisor"}.`,
      });

      setChangingSupervisor(false);
      setSupQuery("");
      await refetchPendingStatus();
      await refetch();
      setStatus("pending");
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  const cancelPendingRequest = async () => {
    if (!pendingRequest?.id) return;
    try {
      await cancelSupervisorRequest(pendingRequest.id).unwrap();
      await refetchPendingStatus();
      await refetch();
      // Keep changingSupervisor=true so search opens immediately after cancel
      if (!changingSupervisor) setStatus("none");
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  // Whole-logbook flags for the calendar, so a review left on week 6 shows on
  // the grid while the candidate is looking at week 20.
  const { data: weeksStatus } = useGetLogbookWeeksStatusQuery(
    { logbookId: String(id) },
    { skip: !id },
  );

  const weeksWithEntries = useMemo(
    () =>
      new Set<number>(
        Array.isArray(weeksStatus?.weeks_with_entries)
          ? weeksStatus.weeks_with_entries.map(Number)
          : weekData?.entries?.length
          ? [selectedWeek]
          : [],
      ),
    [weeksStatus, weekData, selectedWeek],
  );

  const weeksWithOpenReview = useMemo(
    () =>
      new Set<number>(
        Array.isArray(weeksStatus?.weeks_flagged_for_you)
          ? weeksStatus.weeks_flagged_for_you.map(Number)
          : [],
      ),
    [weeksStatus],
  );

  const [respondToReview, { isLoading: respondingToReview }] =
    useRespondToEntryCommentMutation();

  // Answering hands the entry back to the supervisor; it does not clear the
  // review. Only their approval does, which is why this says "send back".
  const doRespondToReview = async (entry: any) => {
    if (!entry?.id) return;
    try {
      await respondToReview({ entryId: String(entry.id) }).unwrap();
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  const canRequestSupervisor =
    (status === "none" || changingSupervisor) &&
    !hasPendingSupervisorRequest &&
    !pendingChecking;

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="mb-4 flex items-center justify-between">
        <button
          className="text-xs border px-3 py-1 rounded bg-white"
          onClick={() => navigate("/logbook")}
        >
          ← Back to Logbooks
        </button>

        <div className="flex items-center gap-2">
          {logbook?.terms_accepted_at && (
            <button
              className="flex items-center gap-1.5 rounded border bg-white px-3 py-1 text-xs hover:bg-gray-50"
              onClick={() => setTermsOpen(true)}
              title="Read the terms of the logbook scheme"
            >
              <BookOpen size={13} className="text-indigo-600" />
              Scheme terms
            </button>
          )}

          <button
            className="text-xs border px-3 py-1 rounded bg-white"
            onClick={() => refetch()}
          >
            Refresh
          </button>
        </div>
      </div>

      <LogbookTermsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        acceptedAt={logbook?.terms_accepted_at}
      />

      {/* Only once the terms are accepted — the gate is the whole page until
          then, and a chat bubble floating over it would be a second thing to
          do before the first one is done. */}
      {logbook?.terms_accepted_at && <LogbookChat logbookId={logbook?.id} />}

      {isLoading && <p className="text-sm">Loading...</p>}

      {error && (
        <div className="text-sm text-red-600 space-y-2">
          <p>{describeApiError(error, "Failed loading Logbook")}</p>
        </div>
      )}

      {!isLoading && !logbook && (
        <div className="bg-white p-6 rounded-xl border">
          <p className="text-sm text-gray-700">
            Logbook not found (it may not belong to this user).
          </p>
        </div>
      )}

      {/* The scheme's terms come before the logbook itself. They set what has
          to be recorded, how often it must be signed and what fails a stage —
          so a candidate who has not accepted them cannot yet be asked to fill
          anything in. Acceptance is per logbook: a new stage is agreed to on
          its own, under whatever edition is then in force. */}
      {logbook && !logbook.terms_accepted_at && (
        <LogbookTermsGate
          logbookId={String(logbook.id)}
          stage={logbook.stage}
          onAccepted={refetch}
        />
      )}

      {logbook && logbook.terms_accepted_at && (
        <div className="flex gap-6">
          {/* LEFT */}
          <div className="w-80 space-y-4">
            <div className="bg-indigo-50 p-5 rounded-xl border">
              <BookOpen size={24} className="text-indigo-600" />
              <h2 className="text-2xl font-black mt-2">
                Stage {logbook.stage || 1}
              </h2>

              <div className="mt-2">
                <StatusBadge status={logbook.status} />
              </div>

              <div className="mt-3 text-xs text-gray-700">
                <div className="font-semibold">{logbook?.diet?.title}</div>
                <div>
                  {safeDate(logbook?.diet?.start_date)} →{" "}
                  {safeDate(logbook?.diet?.end_date)}
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-700">
                Progress:{" "}
                <span className="font-semibold">
                  {Number(logbook.position || 0)} / {Number(logbook.size || 0)}
                </span>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border">
              <h4 className="font-bold text-sm mb-1">Supervisor</h4>
              <p className="text-sm text-gray-800">
                {fullNameFromSupervisor(logbook.supervisor)}
              </p>

              {status === "pending" && (
                <p className="text-xs text-yellow-700 mt-1">
                  Supervision request is pending.
                </p>
              )}

              {status === "none" && (
                <p className="text-xs text-gray-500 mt-1">
                  No supervisor assigned yet.
                </p>
              )}

              {status === "approved" &&
                (logbook.status === "in_progress" || logbook.status === "accepted") &&
                !changingSupervisor && (
                  <button
                    onClick={() => setChangingSupervisor(true)}
                    className="mt-2 text-xs px-3 py-1 rounded border border-indigo-300 text-indigo-600 hover:bg-indigo-50 transition"
                  >
                    Change Supervisor
                  </button>
                )}

              {changingSupervisor && (
                <button
                  onClick={() => { setChangingSupervisor(false); setSupQuery(""); }}
                  className="mt-2 text-xs px-3 py-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Assessment fee — payable once the logbook is submitted for assessment,
                and only while the payment window is open (LOGBOOK_ASSESSMENT_PAYMENT_OPEN). */}
            {String(logbook.status).toLowerCase() === "submitted" &&
              !logbook.assessment_paid &&
              (isAssessmentPaymentOpen(logbook) ? (
                <div className="bg-white p-4 rounded-xl border-2 border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle size={16} className="text-amber-600" />
                    <h4 className="font-bold text-sm">Assessment Fee Due</h4>
                  </div>
                  <p className="text-[11px] text-gray-600 leading-relaxed mb-3">
                    Your logbook has been submitted for assessment. Please pay the
                    assessment fee of{" "}
                    <span className="font-semibold">
                      ₦
                      {Number(
                        logbook.assessment_fee_amount ?? 10000,
                      ).toLocaleString()}
                    </span>{" "}
                    to have it assessed.
                  </p>
                  <div className="relative">
                    {/* Pulsing glow — draws attention to the payment CTA. */}
                    {!payingAssessment && (
                      <span
                        aria-hidden
                        className="absolute -inset-1 rounded-xl bg-amber-400/60 blur-sm animate-pulse pointer-events-none"
                      />
                    )}
                    <button
                      type="button"
                      onClick={handlePayAssessment}
                      disabled={payingAssessment}
                      className="relative w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 transition"
                    >
                      {payingAssessment ? (
                        <>
                          <Loader2 size={14} className="animate-spin" /> Redirecting…
                        </>
                      ) : (
                        <>Make payment for assessment</>
                      )}
                    </button>
                  </div>
                  {assessmentPayError && (
                    <p className="mt-2 text-[11px] text-red-600 flex items-start gap-1">
                      <AlertCircle size={13} className="mt-0.5 shrink-0" />{" "}
                      {assessmentPayError}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-white p-4 rounded-xl border">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock size={16} className="text-gray-500" />
                    <h4 className="font-bold text-sm">Assessment Fee</h4>
                  </div>
                  <p className="text-[11px] text-gray-600 leading-relaxed mb-3">
                    The payment window for the assessment fee has been closed by the
                    Secretariat.
                  </p>
                  <AssessmentPaymentClosedNotice className="w-full" />
                </div>
              ))}

            {(() => {
              const status = String(logbook.status).toLowerCase();
              const isGraded = status === "graded" || Boolean(logbook.graded_at);
              const isDietAssessed = !!logbook.logbook_assessment_diet_id;
              const awaitingApproval = isDietAssessed && !logbook.result_approved_at;

              // Graded → replace the "awaiting assessment" notice with the outcome.
              if (isGraded) {
                if (awaitingApproval) {
                  return (
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-amber-600" />
                        <h4 className="font-bold text-sm text-amber-800">
                          Logbook assessed
                        </h4>
                      </div>
                      <p className="text-[11px] text-amber-700 mt-1">
                        Your logbook has been graded and is awaiting approval by the
                        board. Your result will be available here once approved.
                      </p>
                    </div>
                  );
                }

                // Approved (or legacy) → cite the result.
                return (
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600" />
                      <h4 className="font-bold text-sm text-emerald-800">
                        Logbook graded
                      </h4>
                    </div>
                    <p className="text-[11px] text-emerald-700 mt-1">
                      Your result has been released
                      {logbook.total_score != null ? (
                        <>
                          {" "}
                          — score{" "}
                          <span className="font-semibold">{logbook.total_score}/100</span>
                        </>
                      ) : (
                        "."
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate(`/logbook/${logbook.id}/result`)}
                      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 bg-white text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition"
                    >
                      View Result
                    </button>
                  </div>
                );
              }

              // Not graded yet → the existing "awaiting assessment" notice.
              if (
                logbook.assessment_paid ||
                status === "awaiting_assessment"
              ) {
                return (
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600" />
                      <h4 className="font-bold text-sm text-emerald-800">
                        Assessment fee paid
                      </h4>
                    </div>
                    <p className="text-[11px] text-emerald-700 mt-1">
                      Your logbook is awaiting assessment.
                    </p>
                  </div>
                );
              }

              return null;
            })()}

            {/* Logbook Report (PDF, max 5 pages) */}
            <div className="bg-white p-4 rounded-xl border">
              <div className="flex items-center gap-2 mb-1">
                <FileText size={16} className="text-indigo-600" />
                <h4 className="font-bold text-sm">Logbook Report</h4>
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed mb-3">
                Upload your logbook report as a <span className="font-semibold">PDF</span>.
                The report must <span className="font-semibold">not be more than 5 pages</span>.
              </p>

              {logbook.report_url ? (
                <div className="flex items-center justify-between gap-2 mb-3 p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                  <a
                    href={logbook.report_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1.5 min-w-0"
                  >
                    <CheckCircle2 size={14} className="shrink-0" />
                    <span className="truncate">
                      View report{logbook.report_pages ? ` (${logbook.report_pages} pages)` : ""}
                    </span>
                  </a>
                </div>
              ) : (
                <p className="text-[11px] text-gray-400 italic mb-3">No report uploaded yet.</p>
              )}

              {["in_progress", "accepted"].includes(String(logbook.status).toLowerCase()) ? (
                <>
                  <input
                    ref={reportInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => onPickReport(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => reportInputRef.current?.click()}
                    disabled={uploadingReport}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 transition"
                  >
                    {uploadingReport ? (
                      <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                    ) : (
                      <><Upload size={14} /> {logbook.report_url ? "Replace report" : "Upload report"}</>
                    )}
                  </button>
                </>
              ) : (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 flex items-start gap-1.5">
                  <AlertCircle size={13} className="mt-0.5 shrink-0" />
                  The report can only be uploaded or changed while the logbook is{" "}
                  <span className="font-semibold">in progress</span> or{" "}
                  <span className="font-semibold">accepted</span>.
                </p>
              )}

              {reportMsg && (
                <p className="mt-2 text-[11px] text-emerald-700 flex items-start gap-1">
                  <CheckCircle2 size={13} className="mt-0.5 shrink-0" /> {reportMsg}
                </p>
              )}
              {reportErr && (
                <p className="mt-2 text-[11px] text-red-600 flex items-start gap-1">
                  <AlertCircle size={13} className="mt-0.5 shrink-0" /> {reportErr}
                </p>
              )}
            </div>

            {status === "approved" && (
              <div className="bg-white p-4 rounded-xl border">
                <h4 className="font-bold text-sm">Select Week</h4>
                <WeekDropdown
                  value={selectedWeek}
                  onChange={setSelectedWeek}
                  weeks={logbook.size}
                />
                {weekLoading && (
                  <p className="text-xs text-gray-500 mt-2">Loading week…</p>
                )}
              </div>
            )}

            {status === "approved" && (
              <WeekCalendar
                totalWeeks={Number(logbook.size || 0)}
                value={selectedWeek}
                onChange={setSelectedWeek}
                loading={weekLoading}
                weeksWithEntries={weeksWithEntries as any}
                weeksWithOpenReview={weeksWithOpenReview as any}
                reviewNote="your supervisor is waiting on a change"
              />
            )}
          </div>

          {/* RIGHT */}
          <div className="flex-1 space-y-6">
            {/* Application */}
            <div className="bg-white p-5 rounded-xl border">
              <h3 className="text-sm font-bold mb-3">Application</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Name</div>
                  <div className="font-medium">
                    {logbook?.application?.title}{" "}
                    {logbook?.application?.surname}{" "}
                    {logbook?.application?.other_names}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="font-medium">
                    {logbook?.application?.email || "—"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="font-medium">
                    {logbook?.application?.phone || "—"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Status</div>
                  <div className="font-medium">
                    {logbook?.application?.status || "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Supervisor request */}
            {(status === "none" || (changingSupervisor && (logbook.status === "in_progress" || logbook.status === "accepted"))) && (
              <div className="bg-white p-6 rounded-xl border space-y-4">
                {pendingChecking && (
                  <div className="text-xs text-gray-500">
                    Checking request status…
                  </div>
                )}

                {/* ✅ show pending supervisor details */}
                {hasPendingSupervisorRequest && (
                  <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                    <div className="text-sm text-yellow-900 font-semibold">
                      Supervisor request is pending
                    </div>

                    <div className="mt-2 text-sm text-yellow-900">
                      <div>
                        Supervisor:{" "}
                        <span className="font-semibold">
                          {[
                            pendingSupervisor?.title,
                            pendingSupervisor?.surname,
                            pendingSupervisor?.firstname,
                            pendingSupervisor?.othernames,
                          ]
                            .filter(Boolean)
                            .join(" ") ||
                            pendingSupervisor?.name ||
                            "—"}
                        </span>
                      </div>

                      <div className="text-xs text-yellow-800 mt-1">
                        Membership No:{" "}
                        <span className="font-medium">
                          {pendingSupervisor?.membership_no || "—"}
                        </span>
                      </div>

                      <div className="text-xs text-yellow-800 mt-1">
                        Email:{" "}
                        <span className="font-medium">
                          {pendingSupervisor?.email || "—"}
                        </span>
                      </div>

                      <div className="text-xs text-yellow-800 mt-1">
                        Phone:{" "}
                        <span className="font-medium">
                          {pendingSupervisor?.phone || "—"}
                        </span>
                      </div>

                      <div className="text-xs text-yellow-800 mt-1">
                        Request sent:{" "}
                        <span className="font-medium">
                          {safeDate(pendingRequest?.created_at)}
                        </span>
                      </div>

                      {pendingRequest?.message ? (
                        <div className="text-xs text-yellow-800 mt-2">
                          Message:{" "}
                          <span className="font-medium">
                            {pendingRequest.message}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <button
                      onClick={cancelPendingRequest}
                      disabled={cancelling}
                      className="mt-3 text-xs px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                    >
                      {cancelling ? "Cancelling…" : "Cancel Request & Choose Another"}
                    </button>
                  </div>
                )}

                <p className="text-sm text-gray-800">
                  {changingSupervisor
                    ? "Search for a new supervisor. They must accept before the change takes effect."
                    : "Select a supervisor to unlock logbook entries."}
                </p>

                <input
                  placeholder="Search Supervisor (min 3 chars)..."
                  value={supQuery}
                  onChange={(e) => setSupQuery(e.target.value)}
                  className="border px-3 py-2 rounded w-full disabled:bg-gray-100"
                  disabled={!canRequestSupervisor}
                />

                {supLoading && <p className="text-xs">Searching…</p>}

                {/* ✅ normalized list */}
                {supResults.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded border">
                    {supResults.slice(0, 10).map((m: any) => (
                      <div
                        key={m.id}
                        className="flex items-center justify-between py-2 border-b last:border-b-0"
                      >
                        <div className="text-sm">
                          <div className="font-medium">
                            {m.name ||
                              [m.surname, m.firstname, m.othernames]
                                .filter(Boolean)
                                .join(" ") ||
                              "—"}
                          </div>
                          {m.membership_no && (
                            <div className="text-xs text-gray-500">
                              {m.membership_no}
                            </div>
                          )}
                        </div>

                        <button
                          className="text-xs px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
                          disabled={requesting || !canRequestSupervisor}
                          onClick={() => requestSupervisor(m)}
                        >
                          Request
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {status === "pending" && (
              <div className="bg-white p-6 rounded-xl border space-y-2">
                <h3 className="font-bold text-sm">Pending Approval</h3>
                <p className="text-sm text-gray-700">
                  Your supervisor request has been sent. Once approved, you can
                  start filling weekly entries.
                </p>

                {pendingSupervisor && (
                  <div className="mt-2 p-3 rounded bg-gray-50 border text-sm">
                    <div className="font-semibold text-gray-800">
                      Waiting for:
                    </div>
                    <div className="text-gray-700">
                      {[
                        pendingSupervisor?.title,
                        pendingSupervisor?.surname,
                        pendingSupervisor?.firstname,
                        pendingSupervisor?.othernames,
                      ]
                        .filter(Boolean)
                        .join(" ") ||
                        pendingSupervisor?.name ||
                        "—"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {pendingSupervisor?.membership_no || ""}
                    </div>
                  </div>
                )}

                <button
                  onClick={cancelPendingRequest}
                  disabled={cancelling}
                  className="mt-2 text-xs px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                >
                  {cancelling ? "Cancelling…" : "Cancel Request & Choose Another"}
                </button>
              </div>
            )}

            {/* Approved entries */}
            {status === "approved" && (
              <div className="space-y-4">
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="text-sm font-bold">
                      Week {selectedWeek} Entries
                    </h3>
                    <p className="text-xs text-gray-500">
                      {logbook.status === "in_progress" || logbook.status === "accepted"
                        ? "Fill daily activities and save each day."
                        : "This logbook is read-only."}
                    </p>
                  </div>
                  {saving && <p className="text-xs text-gray-500">Saving…</p>}
                </div>

                {logbook.status !== "in_progress" && logbook.status !== "accepted" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 font-medium">
                    This logbook is <span className="font-black uppercase">{logbook.status}</span> and can no longer be edited.
                  </div>
                )}

                {weekBlocked && !weekLoading && (
                  <div className="bg-white border rounded-xl px-4 py-5 text-center">
                    <p className="text-sm font-semibold text-gray-800">
                      Week {selectedWeek} is not open yet
                    </p>
                    <p className="mt-1 text-xs text-gray-600">{weekBlocked}</p>
                  </div>
                )}

                {!weekBlocked && DAYS.map((day) => {
                  const meta = entryMeta[day];
                  const canEditLogbook =
                    logbook.status === "in_progress" || logbook.status === "accepted";
                  // Approved entries remain editable — re-saving sends them back
                  // to the supervisor for acceptance. Only a closed logbook locks them.
                  const isLocked = !canEditLogbook;
                  const wasApproved = meta?.status === "approved";

                  const entry = weekData?.entries?.find(
                    (e: any) => NUM_TO_DAY[e.day] === day,
                  );

                  const hasEntry = Boolean(entry?.id);

                  return (
                    <div
                      key={day}
                      className="bg-white p-4 rounded-xl border space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{day}</p>
                        {meta?.status && <StatusBadge status={meta.status} />}
                      </div>

                      {hasEntry && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
                          <span>
                            Entry date:{" "}
                            <span className="font-medium text-gray-800">
                              {safeDate(entry?.entry_date)}
                            </span>
                          </span>

                          <span className="flex items-center gap-1">
                            Last updated:{" "}
                            <span className="font-medium text-gray-800">
                              {safeDate(entry?.updated_at)}
                            </span>
                          </span>
                        </div>
                      )}

                      {/* Subject dropdown */}
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                          Subject / Training Code
                        </label>
                        <select
                          className="w-full border p-2 rounded text-sm disabled:bg-gray-100"
                          disabled={isLocked}
                          value={dailySubject[day]}
                          onChange={(e) =>
                            setDailySubject({
                              ...dailySubject,
                              [day]: e.target.value,
                            })
                          }
                        >
                          <option value="">— Select subject —</option>
                          {trainingCodes.map((tc) => (
                            <option key={tc.id} value={String(tc.id)}>
                              {tc.code} — {tc.description}
                            </option>
                          ))}
                        </select>
                        {/* Show selected subject label when locked */}
                        {isLocked && entry?.training_code && (
                          <p className="text-[11px] text-gray-500 mt-1">
                            {entry.training_code.code} —{" "}
                            {entry.training_code.description}
                          </p>
                        )}
                      </div>

                      <textarea
                        rows={3}
                        className="w-full border p-2 rounded disabled:bg-gray-100"
                        disabled={isLocked}
                        value={dailyText[day]}
                        onChange={(e) =>
                          setDailyText({ ...dailyText, [day]: e.target.value })
                        }
                        placeholder={`Write ${day} log...`}
                      />

                      <div className="flex flex-col md:flex-row gap-3">
                        <input
                          type="date"
                          className="border p-2 rounded w-full disabled:bg-gray-100"
                          disabled={isLocked}
                          value={dailyDate[day]}
                          onChange={(e) =>
                            setDailyDate({
                              ...dailyDate,
                              [day]: e.target.value,
                            })
                          }
                        />

                        <input
                          type="number"
                          className="border p-2 rounded md:w-40 w-full disabled:bg-gray-100"
                          disabled={isLocked}
                          value={dailyHours[day]}
                          onChange={(e) =>
                            setDailyHours({
                              ...dailyHours,
                              [day]: e.target.value,
                            })
                          }
                          placeholder="Hours"
                        />

                        <button
                          onClick={() => handleSaveDay(day)}
                          disabled={isLocked || saving}
                          className="bg-indigo-600 text-white px-4 py-2 rounded text-xs disabled:opacity-50"
                        >
                          Save
                        </button>
                      </div>

                      {!isLocked && wasApproved && (
                        <div className="mt-2 rounded bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-800">
                          This entry has been accepted. Editing it will send it
                          back to your supervisor for acceptance again.
                        </div>
                      )}

                      {(isLocked ||
                        meta?.status === "approved" ||
                        meta?.status === "rejected" ||
                        entry?.supervisor_comment) && (
                        <div
                          className={`mt-2 rounded border p-3 text-xs ${
                            entry?.review_state === "awaiting_candidate"
                              ? "border-red-200 bg-red-50"
                              : entry?.review_state === "awaiting_supervisor"
                              ? "border-amber-200 bg-amber-50"
                              : "bg-gray-50"
                          }`}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-semibold text-gray-700">
                              Supervisor Feedback
                            </p>
                            {entry?.review_state === "awaiting_candidate" && (
                              <span className="rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                                Needs your attention
                              </span>
                            )}
                            {entry?.review_state === "awaiting_supervisor" && (
                              <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                                With your supervisor
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-gray-600">
                            {entry?.supervisor_comment ||
                              "No comment provided."}
                          </p>
                          <p className="mt-1 text-[11px] text-gray-500">
                            Last updated: {safeDate(entry?.updated_at)}
                          </p>

                          {/* Editing the day does this on its own, server
                              side. This is for a review that needed no edit.
                              Either way it only hands the entry back — the
                              review ends when the supervisor approves it. */}
                          {entry?.review_state === "awaiting_candidate" && (
                            <button
                              type="button"
                              onClick={() => doRespondToReview(entry)}
                              disabled={respondingToReview}
                              className="mt-2 rounded bg-gray-900 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                            >
                              {respondingToReview
                                ? "Sending…"
                                : "I have addressed this — send back to supervisor"}
                            </button>
                          )}

                          {entry?.review_state === "awaiting_supervisor" && (
                            <p className="mt-2 text-[11px] text-amber-800">
                              Sent back to your supervisor. It clears when they
                              approve this entry.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        open={modal.open}
        title={modal.title}
        onClose={() => setModal({ open: false, title: "", message: "" })}
      >
        {modal.message}
      </Modal>
    </div>
  );
}
